import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  IconCompose,
  IconHistory,
  IconMail,
  Inline,
  Text,
} from 'braid-design-system';
import type { FC } from 'react';

import { IconVerifiedTransparentSVG } from 'src/App/AppPageBlock/SEEKPass/IconVerifiedTransparentSVG';
import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import {
  trackClickedEmailsInCandidateList,
  trackClickedNotesInCandidateList,
  trackClickedPreviousApplicationsInCandidateList,
} from 'src/utils/events';

import translations from '../.vocab';
import type { JobApplicationCardProps } from '../JobApplicationCard';

import { AttachmentButtons } from './AttachmentButtons';

export const AttachmentsAndRecords: FC<
  Pick<
    JobApplicationCardProps,
    | 'attachments'
    | 'jobApplication'
    | 'previousApplicationCount'
    | 'markApplicationAsRead'
    | 'openDetailsDrawer'
    | 'emailCount'
    | 'notesAndAttachmentsCount'
    | 'isVerifiedId'
    | 'jobId'
    | 'verificationsCount'
    | 'onVerificationsIconClick'
  > & {
    homeLocationIdsForTracking: string[];
    nationalityIdsForTracking: string[];
    isMobile: boolean;
  }
> = ({
  attachments,
  isMobile,
  jobApplication,
  previousApplicationCount,
  openDetailsDrawer,
  markApplicationAsRead,
  emailCount,
  notesAndAttachmentsCount,
  isVerifiedId,
  homeLocationIdsForTracking,
  nationalityIdsForTracking,
  jobId,
  verificationsCount,
  onVerificationsIconClick,
}) => {
  const { t } = useTranslations(translations);

  const attachmentButtonIcons =
    attachments.length > 0 ? (
      <AttachmentButtons
        attachments={attachments}
        isMobile={isMobile}
        jobApplication={jobApplication}
        markApplicationAsRead={markApplicationAsRead}
        openDetailsDrawer={openDetailsDrawer}
        isVerifiedId={isVerifiedId}
        homeLocationIdsForTracking={homeLocationIdsForTracking}
        nationalityIdsForTracking={nationalityIdsForTracking}
        jobId={jobId}
      />
    ) : null;

  const verificationsButtonIcon =
    verificationsCount > 0 ? (
      <Inline space="xxsmall" alignY="center">
        <ButtonIcon
          variant="transparent"
          id={`${jobApplication.id}-verifications`}
          icon={<IconVerifiedTransparentSVG title={t('Verified Icon')} />}
          label={t('Verifications')}
          onClick={(e) => {
            e.stopPropagation();
            openDetailsDrawer(JOB_APPLICATION_TAB.VERIFICATIONS);
            onVerificationsIconClick();
          }}
        />
        <Text size="small"> {t('count', { count: verificationsCount })}</Text>
      </Inline>
    ) : null;

  const previousApplicationButtonIcon =
    previousApplicationCount > 0 ? (
      <Inline space="xxsmall" alignY="center">
        <ButtonIcon
          variant="transparent"
          id={`${jobApplication.id}-previous-applications`}
          icon={<IconHistory />}
          label={t('Past applications')}
          onClick={(e) => {
            e.stopPropagation();
            openDetailsDrawer(JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS);
            trackClickedPreviousApplicationsInCandidateList({
              candidateId: jobApplication.candidateId,
              currentPage: TRACKING_CANDIDATE_LIST,
              isVerifiedId,
              searchLocationId: homeLocationIdsForTracking,
              searchNationalityId: nationalityIdsForTracking,
            });
          }}
        />
        <Text size="small">
          {t('count', { count: previousApplicationCount })}
        </Text>
      </Inline>
    ) : null;

  const emailButtonIcon =
    emailCount > 0 ? (
      <Box data-cy="application-card-email">
        <Inline space="xxsmall" alignY="center">
          <ButtonIcon
            variant="transparent"
            id={`${jobApplication.id}-emails`}
            icon={<IconMail />}
            label={t('Emails')}
            onClick={(e) => {
              e.stopPropagation();
              openDetailsDrawer(JOB_APPLICATION_TAB.EMAILS);
              trackClickedEmailsInCandidateList({
                candidateId: jobApplication.candidateId,
                currentPage: TRACKING_CANDIDATE_LIST,
                isVerifiedId,
                searchLocationId: homeLocationIdsForTracking,
                searchNationalityId: nationalityIdsForTracking,
              });
            }}
          />
          <Text size="small"> {t('count', { count: emailCount })}</Text>
        </Inline>
      </Box>
    ) : null;

  const noteButtonIcon =
    notesAndAttachmentsCount > 0 ? (
      <Inline space="xxsmall" alignY="center">
        <ButtonIcon
          variant="transparent"
          id={`${jobApplication.id}-notes`}
          icon={<IconCompose />}
          label={t('Notes and attachments')}
          onClick={(e) => {
            e.stopPropagation();
            openDetailsDrawer(JOB_APPLICATION_TAB.NOTES_ATTACHMENTS);
            trackClickedNotesInCandidateList({
              candidateId: jobApplication.candidateId,
              currentPage: TRACKING_CANDIDATE_LIST,
              isVerifiedId,
              searchLocationId: homeLocationIdsForTracking,
              searchNationalityId: nationalityIdsForTracking,
            });
          }}
        />
        <Text size="small">
          {' '}
          {t('count', { count: notesAndAttachmentsCount })}
        </Text>
      </Inline>
    ) : null;

  return (
    <Inline space="small">
      {attachmentButtonIcons}
      {verificationsButtonIcon}
      {previousApplicationButtonIcon}
      {emailButtonIcon}
      {noteButtonIcon}
    </Inline>
  );
};
